import { t } from '@lingui/core/macro';
import { useCallback, useState } from 'react';

import { type LoginAsInstanceType, useLoginAsInstanceList } from '@/api/organization';
import { GlobalLoader } from '@/components/GlobalLoader';
import {
  InstantSearch,
  InstantSearchSortOrder,
  type QueryState,
  type SolQueryParamsNew,
} from '@/components/InstantSearch';
import { getSolQueryParamsNewFromQueryState } from '@/components/InstantSearch/util/search-util';
import { Page } from '@/components/page';
import { ReturnToLink } from '@/components/ReturnToLink';
import { useLogOut } from '@/hooks/useLogOut';
import { ROUTES } from '@/router';

import LoginAsHeader from './LoginAsHeader';
import LoginAsTable from './LoginAsTable';

const DEFAULT_QUERY_STATE: Partial<QueryState> = {
  size: 20,
  sortBy: 'createdAt',
  sortOrder: InstantSearchSortOrder.DESC,
};

const LoginAs = () => {
  const { clearCacheOnly } = useLogOut();
  const [tableParams, setTableParams] = useState<SolQueryParamsNew>();
  const { instanceListData, totalResults, isLoading, error } = useLoginAsInstanceList(tableParams);
  const [isSwitching, setIsSwitching] = useState(false);
  const handleQueryStateChange = useCallback(
    (queryState: Required<QueryState>) => {
      const tableParams = getSolQueryParamsNewFromQueryState(queryState);
      setTableParams(tableParams);
    },
    [setTableParams],
  );

  const handleLoginAs = (instance: LoginAsInstanceType) => {
    setIsSwitching(true);
    clearCacheOnly();
    // Reload the page to clear all state.
    window.location.href = ROUTES.appByInstanceId(instance.id);
  };

  if (isSwitching) {
    return <GlobalLoader />;
  }

  return (
    <Page title={t`Your Profile`} pageName={t`Login As`} exampleAIQuestions={[]}>
      <div className="flex flex-col gap-6">
        <ReturnToLink route={ROUTES.profile.path} routeName={t`Your Profile`} />
        <InstantSearch
          prefix="l"
          defaultQueryState={DEFAULT_QUERY_STATE}
          onQueryStateChange={handleQueryStateChange}
        >
          <LoginAsHeader />
          <LoginAsTable
            data={instanceListData}
            totalResults={totalResults}
            isLoading={isLoading}
            error={error}
            onLoginAs={handleLoginAs}
          />
        </InstantSearch>
      </div>
    </Page>
  );
};

export default LoginAs;
